import {
  isFullShipping,
  isSecurepayShipping,
} from 'server/detail-requests/ShippingCost/FeatureHelper';
import {
  CategoryID,
  FeatureURI,
  LeafCategoryID,
  type AdItem,
} from '@sbt-web/network/types';
import { isShippable } from '@sbt-web/utils';
import { evalBoolFeature, evalNumberFeature } from '@/shared/models';
import { isPro } from '@/shared/models/Advertiser';

type VisibilityChecker = (ad: AdItem, userId?: string | null) => boolean;

export enum TransactionFlow {
  SendOffer = 'SENDOFFER',
  BuyNow = 'BUYNOW',
}

type TransactionFlowsChecker = (
  ad: AdItem,
  userId?: string | null
) => TransactionFlow[];

const isSold = (ad: AdItem): boolean => {
  const trxStatus = ad.features[FeatureURI.TransactionStatus]?.values[0].value;
  return !!trxStatus;
};

const canBeTransacted = (ad: AdItem): boolean => isShippable(ad) && !isSold(ad);

const isTransactionEnabled: VisibilityChecker = (ad, userId) => {
  const isUserTheAdvertiser = userId == ad.advertiser.userId;
  return canBeTransacted(ad) && !isUserTheAdvertiser;
};

const isFace2FaceAlreadySeen = () => {
  try {
    return localStorage.getItem('F2FSeen') === 'true';
  } catch {
    return false;
  }
};

export const isFaceToFaceAllowed: VisibilityChecker = (ad, userId) => {
  const isUserTheAdvertiser = userId == ad.advertiser.userId;
  const categoryList: LeafCategoryID[] = [
    CategoryID.AccessoriAuto,
    CategoryID.Informatica,
    CategoryID.AudioVideo,
    CategoryID.Telefonia,
    CategoryID.ArredamentoCasalinghi,
    CategoryID.GiardinoFaiDaTe,
    CategoryID.AbbigliamentoAccessori,
    CategoryID.TuttoBambini,
    CategoryID.MusicaFilm,
    CategoryID.Sports,
    CategoryID.Collezionismo,
    CategoryID.AccessoriMoto,
    CategoryID.Elettrodomestici,
    CategoryID.LibriRiviste,
    CategoryID.StrumentiMusicali,
    CategoryID.Fotografia,
    CategoryID.Biciclette,
    CategoryID.ConsoleVideogiochi,
    CategoryID.AccessoriAnimali,
  ];
  const sellerEnabledShipping = evalBoolFeature(
    ad.features[FeatureURI.ItemShippable]
  );
  const price = evalNumberFeature(ad.features[FeatureURI.Price]);
  const isValidPrice = !!price && price > 0 && price <= 2000;
  return (
    !isFace2FaceAlreadySeen() &&
    !isUserTheAdvertiser &&
    categoryList.includes(ad.category.id) &&
    !isPro(ad.advertiser.type) &&
    !sellerEnabledShipping &&
    !isSold(ad) &&
    isValidPrice
  );
};

const isOfferAllowed: VisibilityChecker = (ad) => !isPro(ad.advertiser.type);

const isBuyNowAllowed: VisibilityChecker = (ad) => {
  const price = evalNumberFeature(ad.features[FeatureURI.Price]);
  const maxPrice = isPro(ad.advertiser.type) ? 7000 : 2000;
  const isValidPrice = !!price && price > 0 && price <= maxPrice;
  return isValidPrice;
};

export const getTransactionFlows: TransactionFlowsChecker = (ad, userId) => {
  const transactionFlows: TransactionFlow[] = [];

  if (isTransactionEnabled(ad, userId)) {
    if (isOfferAllowed(ad)) transactionFlows.push(TransactionFlow.SendOffer);
    if (isBuyNowAllowed(ad)) transactionFlows.push(TransactionFlow.BuyNow);
  }
  return transactionFlows;
};

export const isPaypalEnabled = (ad: AdItem): boolean =>
  canBeTransacted(ad) &&
  (isFullShipping(ad) ||
    (isPro(ad.advertiser.type) && isSecurepayShipping(ad)));
